import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Clock, Mail, MessageCircle, Pause, Save, Sparkles, Eye, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BILLING_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
const GRACE_DAYS = [5, 7, 10, 15, 20, 30];

/**
 * Phase 325b — Per-academy auto-billing settings card.
 *
 * Lives inside the Owner Settings page as a new "Billing" tab. Owner
 * controls when their auto-fee cron fires, the grace window, the
 * notification channels, and a pause-everything kill-switch.
 */
export default function BillingSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Phase 325c — Test-fire preview
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/academy/billing-settings');
      setSettings(res.data?.settings || null);
    } catch (err) {
      toast.error('Failed to load billing settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await api.put('/academy/billing-settings', settings);
      setSettings(res.data?.settings || settings);
      toast.success('Billing settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const update = (patch) => setSettings((prev) => ({ ...(prev || {}), ...patch }));

  const handlePreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await api.post('/academy/billing-settings/preview');
      setPreview(res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const formatINR = (n) => {
    if (!n && n !== 0) return '—';
    return n >= 100000
      ? `₹${(n / 100000).toFixed(1)}L`
      : n >= 1000
        ? `₹${(n / 1000).toFixed(1)}K`
        : `₹${Math.round(n).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!settings) return null;

  return (
    <Card data-testid="billing-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-emerald-600" />
          Auto-Bill Settings
        </CardTitle>
        <CardDescription className="text-xs">
          Out-Play auto-generates a monthly fee row for every active student in your academy on the day you pick below.
          Idempotent — the same month is never billed twice. Pause anytime.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Pause */}
        <div className={`flex items-start justify-between rounded-lg p-3 border ${settings.paused ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <div className="flex items-start gap-2">
            <Pause className={`h-4 w-4 mt-0.5 ${settings.paused ? 'text-rose-600' : 'text-emerald-600'}`} />
            <div>
              <Label htmlFor="bs-paused" className="text-sm font-semibold cursor-pointer">
                {settings.paused ? 'Auto-billing is PAUSED' : 'Auto-billing is ACTIVE'}
              </Label>
              <p className="text-[11px] text-slate-600 mt-0.5">
                {settings.paused
                  ? 'No fees will be auto-generated until you turn this off.'
                  : 'Fees auto-generate on day '+settings.billing_day+' of every month at 06:00 IST.'}
              </p>
            </div>
          </div>
          <Switch
            id="bs-paused"
            checked={settings.paused}
            onCheckedChange={(v) => update({ paused: v })}
            data-testid="billing-settings-paused"
          />
        </div>

        {/* Billing day */}
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Billing day of the month
          </Label>
          <Select
            value={String(settings.billing_day)}
            onValueChange={(v) => update({ billing_day: parseInt(v, 10) })}
            disabled={settings.paused}
          >
            <SelectTrigger data-testid="billing-settings-day"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BILLING_DAYS.map((d) => (
                <SelectItem key={d} value={String(d)}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of every month</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500">Choose 1–28 to avoid February month-end issues.</p>
        </div>

        {/* Grace days */}
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1">
            <Clock className="h-3 w-3" /> Grace period (days until due)
          </Label>
          <Select
            value={String(settings.grace_days)}
            onValueChange={(v) => update({ grace_days: parseInt(v, 10) })}
            disabled={settings.paused}
          >
            <SelectTrigger data-testid="billing-settings-grace"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GRACE_DAYS.map((d) => (
                <SelectItem key={d} value={String(d)}>{d} days after billing</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Notifications */}
        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
          <p className="text-xs font-semibold text-slate-700">Parent notifications on generation</p>
          <div className="flex items-center justify-between">
            <Label htmlFor="bs-email" className="flex items-center gap-2 text-sm cursor-pointer">
              <Mail className="h-3.5 w-3.5 text-slate-500" /> Email
            </Label>
            <Switch
              id="bs-email"
              checked={settings.notify_email}
              onCheckedChange={(v) => update({ notify_email: v })}
              disabled={settings.paused}
              data-testid="billing-settings-email-toggle"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="bs-wa" className="flex items-center gap-2 text-sm cursor-pointer">
              <MessageCircle className="h-3.5 w-3.5 text-emerald-600" /> WhatsApp
            </Label>
            <Switch
              id="bs-wa"
              checked={settings.notify_whatsapp}
              onCheckedChange={(v) => update({ notify_whatsapp: v })}
              disabled={settings.paused}
              data-testid="billing-settings-wa-toggle"
            />
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-emerald-600 hover:bg-emerald-700"
          data-testid="billing-settings-save"
        >
          <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Saving…' : 'Save settings'}
        </Button>

        {/* Phase 325c — Test-fire preview */}
        <div className="pt-2 border-t border-slate-200">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={previewing}
            className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="billing-settings-preview"
          >
            <Eye className="h-4 w-4 mr-1.5" /> {previewing ? 'Calculating…' : 'Preview next billing (no charges)'}
          </Button>
          <p className="text-[10px] text-slate-500 mt-1.5 text-center">
            Shows exactly which students will be billed, with amounts. Nothing is saved.
          </p>
        </div>

        {preview && (
          <div className="rounded-lg border border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 p-3 space-y-2" data-testid="billing-settings-preview-result">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" />
                Preview · {preview.period}
              </p>
              <p className="text-xs font-mono text-emerald-700">Due {preview.due_date}</p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white rounded-md p-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Would Bill</p>
                <p className="text-lg font-bold text-emerald-700" data-testid="preview-would-bill-count">{preview.would_bill?.length || 0}</p>
              </div>
              <div className="bg-white rounded-md p-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Total ₹</p>
                <p className="text-lg font-bold text-emerald-700" data-testid="preview-total-amount">{formatINR(preview.total_amount || 0)}</p>
              </div>
              <div className="bg-white rounded-md p-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Already Billed</p>
                <p className="text-lg font-bold text-slate-600">{preview.duplicates?.length || 0}</p>
              </div>
            </div>

            {preview.would_bill?.length > 0 ? (
              <div className="max-h-44 overflow-y-auto rounded-md bg-white border border-emerald-200 divide-y divide-emerald-100">
                {preview.would_bill.map((s, i) => (
                  <div key={s.student_id} className="flex items-center px-2 py-1.5 text-xs gap-2" data-testid={`preview-row-${i}`}>
                    <span className="text-slate-700 truncate flex-1">{s.student_name}</span>
                    {s.custom_id && <span className="font-mono text-[10px] text-slate-500">{s.custom_id}</span>}
                    <span className="font-semibold text-emerald-700 font-mono">₹{Math.round(s.amount).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                {preview.duplicates?.length > 0
                  ? `All ${preview.duplicates.length} eligible students are already billed for ${preview.period}.`
                  : `No students eligible to bill. Make sure your active students have base_fee set or are linked to a course.`}
              </p>
            )}

            {preview.ineligible_count > 0 && (
              <p className="text-[10px] text-slate-500">
                {preview.ineligible_count} students skipped (inactive, zero base_fee, or no course).
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
